import { VoiceFormWriter } from "../utils/timesheet_voice_apply"
import { namedInput } from "../utils/timesheet_voice_form"
import { restoreForm, snapshotForm } from "../utils/timesheet_voice_snapshot"
import { VoiceCardController } from "../utils/voice_card_controller"

// Voice input card on the timesheet create forms (ConfigurableTimeSheets::
// VoiceInputComponent). The card itself — recording, the two requests, states,
// tags, hints, undo — is VoiceCardController; this controller knows the
// timesheet form: VoiceFormWriter puts the suggestions onto it (rows through the
// multi-site-booking / multi-entry controllers), the whole form is snapshotted
// for "Rückgängig", and the summary lists the day values and the rows touched.
const ROW_NAME = /^(site_allocations|additional_entries)\[\d+\]/
const SUMMARY_CORE_KEYS = ["reported_date", "time_from", "time_to", "time_break", "hourly_wage"]

export default class extends VoiceCardController {
  static values = {
    templateId: Number,
  }

  // Kept for callers and tests that drive the form directly.
  applySuggestions(payload) {
    return this.writer().applySuggestions(payload)
  }

  serializeDraft() {
    return this.writer().serializeDraft()
  }

  // --- VoiceCardController hooks ---

  extractRequest(token) {
    const writer = this.writer()
    // An answer always sends the form: the server holds the answer to its field
    // by diffing against it (a first-pass answer would otherwise apply everything).
    const correction = this.hasAppliedOnce || Boolean(this.answering)
    const body = { transcript_token: token, timesheet_template_id: this.templateIdValue }
    if (correction) body.draft = JSON.stringify(writer.serializeDraft())
    const employeeId = writer.selectedEmployeeId()
    if (employeeId) body.employee_id = employeeId
    // The form's day is the reference for "heute"/"gestern" — without it the server
    // resolves them against today and moves an entry for another day to today.
    const reportedDate = writer.findFormInput("reported_date")?.value
    if (reportedDate) body.date = reportedDate
    return { body, correction }
  }

  // The required-field check of the Rückfragen sends the form as it is now.
  questionsRequest() {
    const writer = this.writer()
    const body = {
      timesheet_template_id: this.templateIdValue,
      draft: JSON.stringify(writer.serializeDraft()),
    }
    const employeeId = writer.selectedEmployeeId()
    if (employeeId) body.employee_id = employeeId
    return body
  }

  applyAnswer(question, value) {
    return this.writer().applyAnswer(question.target, value, { replacing: question.selected })
  }

  answerFieldName(question) {
    return this.writer().fieldNameFor(question.target)
  }

  takeSnapshot() {
    return snapshotForm(this.form(), this.controllers())
  }

  restoreSnapshot(snapshot) {
    restoreForm(this.form(), snapshot, this.controllers())
  }

  applyPayload(payload) {
    return this.writer().applySuggestions(payload)
  }

  // Day values as tiles, the rows (sites / blocks) the pass touched as a list.
  renderSummary(changed) {
    const form = this.form()
    const chips = changed
      .filter((name) => !ROW_NAME.test(name))
      .map((name) => [name, this.keyOf(name)])
      .filter(
        ([name, key]) =>
          key && (SUMMARY_CORE_KEYS.includes(key) || !name.startsWith("configurable_time_sheet[")),
      )
      .map(([name, key]) => this.chip(this.label(key), this.displayValue(form, name)))
      .filter(Boolean)

    const rows = this.summaryRows(changed)
    const parts = []
    if (chips.length) {
      const grid = document.createElement("div")
      grid.className = "grid grid-cols-2 gap-2 sm:grid-cols-3"
      grid.append(...chips)
      parts.push(grid)
    }
    if (rows.length) {
      const list = document.createElement("ul")
      list.className = "m-0 list-none overflow-hidden rounded-xl border border-gray-200 p-0"
      list.append(...rows)
      parts.push(list)
    }
    this.summaryTarget.replaceChildren(...parts)
  }

  // --- Timesheet summary ---

  summaryRows(changed) {
    const touched = new Set(changed.map((name) => name.match(ROW_NAME)?.[0]).filter(Boolean))
    const { booking, entries } = this.controllers()
    const writer = this.writer()
    const rows = []
    booking?.allocationTargets.forEach((row, index) => {
      const prefix = writer.rowPrefix(row, "site_allocations")
      if (!touched.has(prefix)) return
      const hours = namedInput(row, `${prefix}[hours]`)?.value
      const amount =
        row === booking.autoAllocation || !hours
          ? this.message("restOfDay")
          : this.message("rowHours").replace("{n}", String(hours).replace(".", ","))
      rows.push(
        this.row(
          index + 1,
          this.selectText(row, `${prefix}[site_id]`),
          this.rowDetail(changed, prefix),
          amount,
        ),
      )
    })
    entries?.entryTargets.slice(1).forEach((block, index) => {
      const prefix = entries.entryPrefix(block)
      if (!touched.has(prefix)) return
      const from = namedInput(block, `${prefix}[time_from]`)?.value || ""
      const to = namedInput(block, `${prefix}[time_to]`)?.value || ""
      const site = this.selectText(block, `${prefix}[site_id]`)
      rows.push(
        this.row(
          index + 2,
          site || `${from}–${to}`,
          this.rowDetail(changed, prefix),
          site ? `${from}–${to}` : "",
        ),
      )
    })
    return rows
  }

  rowDetail(changed, prefix) {
    const form = this.form()
    return changed
      .filter(
        (name) =>
          name.startsWith(`${prefix}[custom_fields]`) || name.startsWith(`${prefix}[allowances]`),
      )
      .map((name) => {
        const value = this.displayValue(form, name)
        return value ? `${this.label(this.keyOf(name))}: ${value}` : null
      })
      .filter(Boolean)
      .join(" · ")
  }

  row(number, title, detail, amount) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    row.querySelector("[data-row-number]").textContent = String(number)
    row.querySelector("[data-row-title]").textContent = title || "–"
    row.querySelector("[data-row-detail]").textContent = detail
    row.querySelector("[data-row-amount]").textContent = amount
    return row
  }

  // The field key a value name belongs to: configurable_time_sheet[time_from] ->
  // time_from, custom_fields[x] / …[custom_fields][x] -> x, allowances[x][input] -> x.
  keyOf(name) {
    const custom = name.match(/custom_fields\]?\[([^\]]+)\]/)
    if (custom) return custom[1]
    const allowance = name.match(/allowances\]?\[([^\]]+)\]\[input\]/)
    if (allowance) return allowance[1]
    if (/allowances\]?\[[^\]]+\]\[/.test(name)) return null
    return name.match(/^configurable_time_sheet\[([^\]]+)\]$/)?.[1] || null
  }

  displayValue(scope, name) {
    const element = namedInput(scope, name)
    if (!element) return ""
    if (element.type === "checkbox") return element.checked ? this.message("yes") : ""
    if (element.tagName === "SELECT") return this.selectText(scope, name)
    if (/\[reported_date\]$/.test(name) && /^\d{4}-\d{2}-\d{2}$/.test(element.value)) {
      const [year, month, day] = element.value.split("-")
      return `${day}.${month}.${year}`
    }
    return element.value.length > 40 ? `${element.value.slice(0, 40)}…` : element.value
  }

  selectText(scope, name) {
    const select = scope.querySelector(`select[name="${name}"], select[name="${name}[]"]`)
    if (!select) return ""
    return Array.from(select.selectedOptions)
      .filter((option) => option.value !== "")
      .map((option) => option.textContent.trim())
      .join(", ")
  }

  writer() {
    return new VoiceFormWriter(this.form(), this.application)
  }

  controllers() {
    const writer = this.writer()
    return {
      booking: writer.rowController("multi-site-booking"),
      entries: writer.rowController("multi-entry"),
      nestedFormFor: (scope) => writer.nestedFormIn(scope),
      setSelect: (select, value) => writer.setSelectValue(select, value),
    }
  }
}
